import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import TeacherDetailModal from "../../components/TeacherDetailModal";
import SectionRoster from "./SectionRoster";
import { syLabel } from "../../utils/schoolYear";
import s from "./ClassOfferingDetailModal.module.css";

function splitSchedule(raw) {
  if (!raw) return { days: "", time: "" };
  const idx = raw.lastIndexOf(" ");
  if (idx === -1) return { days: raw, time: "" };
  return { days: raw.slice(0, idx), time: raw.slice(idx + 1) };
}

/**
 * Modal shown when an admin clicks a class offering anywhere in the
 * academics flows (Room detail, Section detail's classes tab, etc.).
 * Surfaces: subject, teacher (clickable → TeacherDetailModal), section
 * (clickable → /academics/sections/:id), schedule + room, and the
 * full roster of students enrolled in that section for the offering's
 * term — each student card opens StudentDetailModal via SectionRoster.
 *
 * Read-only.
 *
 * Props:
 *   open         boolean
 *   offering     ClassOffering row (must have id, subjectId, sectionId, semesterId, ...)
 *   subjects     Array<Subject>
 *   sections     Array<Section>
 *   gradeLevels  Array<GradeLevel>
 *   employees    Array<Employee>   — for teacher resolution
 *   schoolYears  Array<SchoolYear>
 *   semesters    Array<Semester>
 *   rooms        Array<Room>
 *   offerings    Array<ClassOffering> — the full list, used by the
 *                                       embedded TeacherDetailModal to
 *                                       show the teacher's other classes
 *   students     Array<Student>
 *   enrollments  Array<Enrollment>
 *   onClose      fn()
 */
export default function ClassOfferingDetailModal({
  open,
  offering,
  subjects   = [],
  sections   = [],
  gradeLevels = [],
  employees   = [],
  schoolYears = [],
  semesters   = [],
  rooms       = [],
  offerings   = [],
  students    = [],
  enrollments = [],
  onClose,
}) {
  const [activeTeacher, setActiveTeacher] = useState(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  const subject = useMemo(
    () => offering?.subjectId ? subjects.find(x => x.id === offering.subjectId) : null,
    [offering?.subjectId, subjects]
  );
  const section = useMemo(
    () => offering?.sectionId ? sections.find(x => x.id === offering.sectionId) : null,
    [offering?.sectionId, sections]
  );
  const grade = useMemo(
    () => section?.gradeLevelId ? gradeLevels.find(x => x.id === section.gradeLevelId) : null,
    [section?.gradeLevelId, gradeLevels]
  );
  const teacher = useMemo(
    () => offering?.teacherStaffId ? employees.find(x => x.id === offering.teacherStaffId) : null,
    [offering?.teacherStaffId, employees]
  );
  const semester = useMemo(
    () => offering?.semesterId ? semesters.find(x => x.id === offering.semesterId) : null,
    [offering?.semesterId, semesters]
  );
  const sy = useMemo(
    () => semester?.schoolYearId ? schoolYears.find(x => x.id === semester.schoolYearId) : null,
    [semester?.schoolYearId, schoolYears]
  );
  const room = useMemo(
    () => offering?.roomId ? rooms.find(x => x.id === offering.roomId) : null,
    [offering?.roomId, rooms]
  );

  if (!open || !offering) return null;

  const { days, time } = splitSchedule(offering.schedule);
  const subjectTitle = subject?.title || "—";
  const subjectCode  = subject?.code  || "?";
  const sectionLabel = section
    ? (grade ? `${grade.name} · ${section.name}` : section.name)
    : "—";

  return createPortal(
    <div
      className={s.backdrop}>
      <div
        className={s.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cod-title">
        <div className={s.head}>
          <div className={s.codeChip} aria-hidden="true">{subjectCode}</div>
          <div className={s.headMain}>
            <div className={s.eyebrow}>Class offering</div>
            <h3 id="cod-title" className={s.title}>{subjectTitle}</h3>
            <div className={s.subtitle}>
              {sectionLabel}
              {sy?.label && ` · ${syLabel(sy.label)}`}
              {semester?.label && ` · ${semester.label}`}
            </div>
          </div>
          <button
            type="button"
            className={s.closeBtn}
            onClick={onClose}
            aria-label="Close">
            ✕
          </button>
        </div>

        <div className={s.body}>
          <div className={s.chips}>
            {teacher ? (
              <button
                type="button"
                className={s.chipBtn}
                onClick={() => setActiveTeacher(teacher)}
                title="View teacher details">
                👤 <span>{teacher.name}</span>
              </button>
            ) : (
              <span className={s.chipMissing}>No teacher assigned</span>
            )}
            {section && (
              <Link
                to={`/academics/sections/${section.id}`}
                className={s.chipBtn}
                onClick={onClose}
                title="Open section detail page">
                🏷 <span>{sectionLabel}</span>
              </Link>
            )}
            {room ? (
              <span className={s.chip}>
                📍 {room.name}
                {room.building ? ` · ${room.building}` : ""}
              </span>
            ) : offering.room ? (
              <span className={s.chip}>📍 {offering.room}</span>
            ) : (
              <span className={s.chipMissing}>No room set</span>
            )}
            {(days || time) && (
              <span className={s.chip}>
                🕒 {days}{days && time ? " · " : ""}{time}
              </span>
            )}
            {subject?.units != null && (
              <span className={s.chip}>{subject.units} unit{Number(subject.units) === 1 ? "" : "s"}</span>
            )}
          </div>

          {section && (
            <div className={s.section}>
              <div className={s.sectionHead}>Roster</div>
              <SectionRoster
                sectionId={section.id}
                schoolYearId={sy?.id ?? null}
                semesterId={semester?.id ?? null}
                students={students}
                enrollments={enrollments}
                sections={sections}
                gradeLevels={gradeLevels}
                schoolYears={schoolYears}
                semesters={semesters}
                rooms={rooms}
                onManage={() => { /* hidden via hideManageButton */ }}
                defaultOpen
                hideDisclosure
                hideManageButton
              />
            </div>
          )}
        </div>

        <div className={s.foot}>
          <button type="button" className={s.btnGhost} onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      <TeacherDetailModal
        open={!!activeTeacher}
        teacher={activeTeacher}
        offerings={offerings}
        subjects={subjects}
        sections={sections}
        gradeLevels={gradeLevels}
        semesters={semesters}
        activeSyId={sy?.id ?? null}
        activeSemId={semester?.id ?? null}
        onClose={() => setActiveTeacher(null)}
      />
    </div>,
    document.body
  );
}
